import { Component, inject, Input, signal } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { CustomSymbologyComponent } from '@components/custom-symbology/custom-symbology.component'
import { DialogWarnComponent } from '@components/dialogs/dialog-warn/dialog-warn.component'
import { DialogPersonalLayersComponent } from '@components/panel/tabs/layers-tab/personal-layers/dialog-personal-layers/dialog-personal-layers.component'
import { URL_LEGEND } from '@core/constants/constant.data'
import { isOM4ALayerType } from '@core/constants/scenario-explorer.constants'
import { LayerUploaded } from '@core/models/layers.model'
import { GeojsonEditorService } from '@core/services/geojson-editor.service'
import { UploadedLayersService } from '@core/services/layers-uploaded.service'
import { FormatSharedWithPipe } from '@shared/pipes/format-shared-with.pipe'

@Component({
  selector: 'app-card-layer-personal',
  templateUrl: './card-layer-personal.component.html',
  imports: [
    // Material
    MatTooltipModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatIcon,
    MatButtonModule,
    FormatSharedWithPipe,
    CustomSymbologyComponent,
  ],
})
export class CardLayerPersonalComponent {
  @Input({ required: true }) layer!: LayerUploaded
  uploadedLayersService = inject(UploadedLayersService)
  private readonly _geojsonEditorService = inject(GeojsonEditorService)
  private readonly _dialog = inject(MatDialog)
  imageUrl = URL_LEGEND
  selectedFileToUpdate$ = signal<LayerUploaded | null>(null)
  isDownloadLoading$ = signal<Boolean>(false)
  isDeleteLoading$ = signal<Boolean>(false)
  symbology = null //should be a signal

  async handleCardCheckboxChange() {
    // openmod scenario explorer only
    if (this.layer && isOM4ALayerType(this.layer.layer_type) && this.layer.name.endsWith('.csv')) {
      // fetch symbology
      this.symbology = await this.uploadedLayersService.getCSVSymbology(this.layer)
    }
    this.uploadedLayersService.actionLayer('personal', this.layer)
  }

  async download(upFile: LayerUploaded) {
    this.isDownloadLoading$.set(true)

    try {
      const response = await this.uploadedLayersService.downloadPersonalLayer(upFile.id)

      if (!response || response.size === 0) {
        return
      }

      const url = URL.createObjectURL(response)
      const a = document.createElement('a')

      a.href = url
      a.download = upFile.name
      document.body.appendChild(a)
      a.click()
      a.remove()

      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } finally {
      this.isDownloadLoading$.set(false)
    }
  }

  deleteLayerWithConfirm(uploadedFile: LayerUploaded): void {
    let title = 'Delete personal layer'
    const sharedWith = uploadedFile.shared_with
    if (sharedWith && sharedWith.length > 0) {
      title = 'Delete shared personal layer'
    }

    let subtitle = ''
    if (sharedWith && sharedWith.length == 1) {
      subtitle = 'This layer will no longer be accessible to the user it was shared with.'
    } else if (sharedWith && sharedWith.length > 0) {
      subtitle = `This layer will no longer be accessible to the users (${sharedWith.length})
      it was shared with.`
    }

    const question =
      `Are you sure you want to delete the personal layer ${uploadedFile.name} ? ` + subtitle

    const data = {
      title: title,
      message: question,
    }
    const dialogRef = this._dialog.open(DialogWarnComponent, {
      data: data,
    })

    dialogRef.afterClosed().subscribe((deletionConfirm) => {
      if (deletionConfirm) {
        this._deletePersonalLayer(uploadedFile)
      }
    })
  }

  private async _deletePersonalLayer(layer: LayerUploaded) {
    this.isDeleteLoading$.set(true)
    const response = await this.uploadedLayersService.deletePersonalLayer(layer.id)
    this.isDeleteLoading$.set(false)

    if (!response) return
    this.uploadedLayersService.removeUploadedLayerFromMap(layer)
    this.uploadedLayersService.getUserPersonalAndSharedLayers()
  }

  editPersonalLayer(uploadedFile: LayerUploaded) {
    this.selectedFileToUpdate$.set(uploadedFile)
    const uploadToEdit = {
      id: uploadedFile.id,
      name: uploadedFile.name,
      layerName: uploadedFile.layer,
      shared_with: uploadedFile.shared_with?.map((item: string) => item).join('; '),
    }

    const dialogRef = this._dialog.open(DialogPersonalLayersComponent, {
      width: '50vw',
      data: uploadToEdit,
    })

    dialogRef.afterClosed().subscribe((update) => {
      if (update) {
        this.uploadedLayersService.getUserPersonalAndSharedLayers()
      }
    })
  }

  async openGeojsonEditorDialog() {
    this._geojsonEditorService.openEditorDialog(this.layer)
  }
}
